import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Play, 
  MoreVertical, 
  Edit, 
  Eye, 
  SkipForward, 
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  GripVertical
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ApplicationPreviewModal } from './ApplicationPreviewModal';

interface QueueJob {
  id: string;
  job_id: string;
  job_data: any;
  status: string;
  priority: number;
  match_score: number;
  error_message?: string;
  applied_at?: string;
}

interface QueueManagementProps {
  onStatsUpdate?: () => void;
}

export function QueueManagement({ onStatsUpdate }: QueueManagementProps) {
  const [activeTab, setActiveTab] = useState('ready');
  const [jobs, setJobs] = useState<QueueJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<QueueJob | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [batchProcessing, setBatchProcessing] = useState(false);

  useEffect(() => {
    loadJobs(activeTab);
  }, [activeTab]);

  const loadJobs = async (status: string) => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const statusMap: Record<string, string[]> = {
        ready: ['queued'],
        progress: ['processing'],
        applied: ['applied'],
        failed: ['failed', 'skipped'],
      };

      const { data, error } = await supabase
        .from('auto_apply_queue')
        .select('*')
        .eq('user_id', user.id)
        .in('status', statusMap[status])
        .order('priority', { ascending: false });

      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
      console.error('Error loading jobs:', error);
      toast.error('Failed to load queue');
    } finally {
      setLoading(false);
    }
  };

  const handleBatchApply = async () => {
    if (!confirm(`Apply to all ${jobs.length} jobs in queue?`)) return;

    try {
      setBatchProcessing(true);
      toast.error('Batch auto-apply has been replaced. Please use Smart Apply → Chrome Extension workflow.', { 
        id: 'batch',
        duration: 5000 
      });
    } catch (error: any) {
      console.error('Error in batch apply:', error);
      toast.error(error.message || 'Batch processing failed', { id: 'batch' });
    } finally {
      setBatchProcessing(false);
    }
  };

  const handleApplyNow = async (jobId: string) => {
    try {
      toast.error('Auto-apply has been replaced. Please use Smart Apply → Chrome Extension workflow.', { 
        id: jobId,
        duration: 5000 
      });
    } catch (error: any) {
      console.error('Error applying:', error);
    }
  };

  const handleSkip = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('auto_apply_queue')
        .update({ status: 'skipped' })
        .eq('id', jobId);

      if (error) throw error;

      toast.success('Job skipped');
      loadJobs(activeTab);
    } catch (error) {
      console.error('Error skipping job:', error);
      toast.error('Failed to skip job');
    }
  };

  const handleRemove = async (jobId: string) => {
    try {
      const { error } = await supabase
        .from('auto_apply_queue')
        .delete()
        .eq('id', jobId);

      if (error) throw error;

      toast.success('Job removed from queue');
      loadJobs(activeTab);
    } catch (error) {
      console.error('Error removing job:', error);
      toast.error('Failed to remove job');
    }
  };

  const getMatchScoreBadge = (score: number) => {
    if (score >= 85) return <Badge className="bg-green-500">Match: {score}%</Badge>;
    if (score >= 70) return <Badge className="bg-yellow-500">Match: {score}%</Badge>;
    return <Badge className="bg-red-500">Match: {score}%</Badge>;
  };

  const renderJobCard = (job: QueueJob) => {
    const jobData = job.job_data;

    return (
      <Card key={job.id} className="p-6 hover:shadow-lg transition-shadow">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <GripVertical className="h-4 w-4 text-muted-foreground cursor-move" />
              <h3 className="text-lg font-semibold">{jobData.title}</h3>
            </div>
            <p className="text-muted-foreground">{jobData.company}</p>
            <p className="text-sm text-muted-foreground">{jobData.location}</p>
          </div>

          <div className="flex items-center gap-2">
            {job.match_score && getMatchScoreBadge(job.match_score)}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => {
                  setSelectedJob(job);
                  setShowPreviewModal(true);
                }}>
                  <Eye className="mr-2 h-4 w-4" />
                  Preview Application
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleApplyNow(job.id)}>
                  <Play className="mr-2 h-4 w-4" />
                  Apply Now
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSkip(job.id)}>
                  <SkipForward className="mr-2 h-4 w-4" />
                  Skip This Job
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleRemove(job.id)}
                  className="text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Remove from Queue
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {activeTab === 'ready' && (
          <div className="flex items-center justify-between mt-4">
            <div className="flex items-center gap-2 text-sm text-green-600">
              <CheckCircle className="h-4 w-4" />
              Ready to apply
            </div>
            <Button size="sm" onClick={() => handleApplyNow(job.id)}>
              <Play className="mr-2 h-4 w-4" />
              Apply Now
            </Button>
          </div>
        )}

        {activeTab === 'progress' && (
          <div className="mt-4 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Clock className="h-4 w-4 text-yellow-500 animate-pulse" />
              <span>Processing...</span>
            </div>
            <div className="space-y-1 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-3 w-3 text-green-500" />
                Job description analyzed
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-3 w-3 text-green-500" />
                Keywords extracted
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3 text-yellow-500" />
                Generating resume...
              </div>
            </div>
          </div>
        )}

        {activeTab === 'applied' && job.applied_at && (
          <div className="flex items-center gap-2 mt-4 text-sm text-green-600">
            <CheckCircle className="h-4 w-4" />
            Applied {new Date(job.applied_at).toLocaleDateString()}
          </div>
        )}

        {activeTab === 'failed' && job.error_message && (
          <div className="mt-4 p-3 bg-destructive/10 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-destructive mb-2">
              <XCircle className="h-4 w-4" />
              <span className="font-medium">Error:</span>
            </div>
            <p className="text-sm text-muted-foreground">{job.error_message}</p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => handleApplyNow(job.id)}>
              Retry
            </Button>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div>
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold mb-2">Your Smart Apply Queue</h2>
            <p className="text-muted-foreground">Jobs AI will apply to automatically</p>
          </div>
          {activeTab === 'ready' && jobs.length > 0 && (
            <Button 
              size="lg" 
              onClick={handleBatchApply}
              disabled={batchProcessing}
              className="gap-2"
            >
              <Play className="w-4 h-4" />
              {batchProcessing ? 'Processing...' : `Apply to All (${jobs.length})`}
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="ready">Ready to Apply</TabsTrigger>
          <TabsTrigger value="progress">In Progress</TabsTrigger>
          <TabsTrigger value="applied">Applied</TabsTrigger>
          <TabsTrigger value="failed">Failed/Review</TabsTrigger>
        </TabsList>

        <TabsContent value="ready" className="mt-6 space-y-4">
          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : jobs.length === 0 ? (
            <Card className="p-12 text-center">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No jobs in queue</h3>
              <p className="text-muted-foreground">Save jobs from Job Search to add them to your Smart Apply queue</p>
            </Card>
          ) : (
            jobs.map(renderJobCard)
          )}
        </TabsContent>

        <TabsContent value="progress" className="mt-6 space-y-4">
          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : jobs.length === 0 ? (
            <Card className="p-12 text-center">
              <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No applications in progress</h3>
              <p className="text-muted-foreground">Applications will appear here while being processed</p>
            </Card>
          ) : (
            jobs.map(renderJobCard)
          )}
        </TabsContent>

        <TabsContent value="applied" className="mt-6 space-y-4">
          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : jobs.length === 0 ? (
            <Card className="p-12 text-center">
              <CheckCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No applications yet</h3>
              <p className="text-muted-foreground">Successfully applied jobs will appear here</p>
            </Card>
          ) : (
            jobs.map(renderJobCard)
          )}
        </TabsContent>

        <TabsContent value="failed" className="mt-6 space-y-4">
          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : jobs.length === 0 ? (
            <Card className="p-12 text-center">
              <XCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No failed applications</h3>
              <p className="text-muted-foreground">Applications requiring review will appear here</p>
            </Card>
          ) : (
            jobs.map(renderJobCard)
          )}
        </TabsContent>
      </Tabs>

      {selectedJob && (
        <ApplicationPreviewModal
          open={showPreviewModal}
          onOpenChange={setShowPreviewModal}
          job={selectedJob}
          onApprove={() => {
            handleApplyNow(selectedJob.id);
            setShowPreviewModal(false);
          }}
        />
      )}
    </div>
  );
}
